import React, { useEffect, useState } from 'react';
import { BASE_URL } from '../config/api'; // Ensure BASE_URL is defined in your config/api.js
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const LeaveList = () => {
    const [leaves, setLeaves] = useState([]);
    const [filteredLeaves, setFilteredLeaves] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [startDateFilter, setStartDateFilter] = useState('');
    const [endDateFilter, setEndDateFilter] = useState('');
    const [staffDetails, setStaffDetails] = useState({}); // To store staff data
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [selectedLeaveId, setSelectedLeaveId] = useState(null);
    const [newStatus, setNewStatus] = useState('');
    const navigate = useNavigate();

    // Fetch leaves and staff details (you might want to combine this if your API provides the data together)
    useEffect(() => {
        fetch(`${BASE_URL}leaves`) // Adjust the endpoint if needed
            .then((response) => response.json())
            .then((data) => {
                if (data.status === 1) {
                    setLeaves(data.data);
                    setFilteredLeaves(data.data);

                    // Fetch staff details after loading the leaves
                    const staffIds = [...new Set(data.data.map(leave => leave.staff_id))];
                    fetchStaffDetails(staffIds);
                } else {
                    console.error('Error fetching leaves:', data.message);
                }
            })
            .catch((error) => console.error('Error fetching leaves:', error));
    }, []);

    const fetchStaffDetails = (staffIds) => {
        // Assuming you have a staff details endpoint like `/staff/{staff_id}`
        const staffPromises = staffIds.map(staffId =>
            fetch(`${BASE_URL}staff/${staffId}`)
                .then((response) => response.json())
                .then((data) => {
                    if (data.status === 1) {
                        return { id: staffId, name: data.data.name };
                    } else {
                        console.error(`Error fetching staff with ID ${staffId}`);
                        return { id: staffId, name: 'Unknown' };
                    }
                })
        );

        // Once all staff data is fetched, update the state
        Promise.all(staffPromises).then((staffs) => {
            const staffData = staffs.reduce((acc, staff) => {
                acc[staff.id] = staff.name;
                return acc;
            }, {});
            setStaffDetails(staffData);
        });
    };

    const handleSearchChange = (event) => {
        const term = event.target.value;
        setSearchTerm(term);

        const filtered = leaves.filter((leave) =>
            leave.reason.toLowerCase().includes(term.toLowerCase())
        );
        setFilteredLeaves(filtered);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = ('0' + date.getDate()).slice(-2); // Add leading zero if day < 10
        const month = ('0' + (date.getMonth() + 1)).slice(-2); // Add leading zero if month < 10
        const year = date.getFullYear(); // Get full year (YYYY)
        return `${day}/${month}/${year}`;
    };

    const handleEdit = (id) => {
        navigate(`/EditLeave/${id}`);
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this leave?')) {
            fetch(`${BASE_URL}leaves/${id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then((response) => response.json())
                .then((data) => {
                    if (data.status === 1) {
                        setLeaves(leaves.filter((leave) => leave.id !== id));
                        setFilteredLeaves(filteredLeaves.filter((leave) => leave.id !== id));
                    }
                })
                .catch((error) => console.error('Error deleting leave:', error));
        }
    };

    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.text('Leave List', 14, 10);

        // Generate table data
        const tableData = filteredLeaves.map((leave) => [
            staffDetails[leave.staff_id] || 'N/A', // Display staff name

            new Date(leave.start_date).toLocaleDateString(),
            new Date(leave.end_date).toLocaleDateString(),
            leave.reason || 'N/A',
            leave.status || 'N/A',
        ]);

        doc.autoTable({
            head: [['Staff Name', 'Start Date', 'End Date', 'Reason', 'Status']],
            body: tableData,
        });

        doc.save('LeaveList.pdf');
    };

    const handleFilterByDate = () => {
        let filtered = leaves;

        // Function to remove time from Date for accurate comparison
        const formatDate = (date) => new Date(new Date(date).setHours(0, 0, 0, 0));

        // Apply date filters if specified
        if (startDateFilter) {
            filtered = filtered.filter((leave) => formatDate(leave.start_date) >= formatDate(startDateFilter));
        }
        if (endDateFilter) {
            filtered = filtered.filter((leave) => formatDate(leave.end_date) <= formatDate(endDateFilter));
        }

        setFilteredLeaves(filtered);
    };

    const openStatusModal = (id) => {
        setSelectedLeaveId(id);
        setStatusModalOpen(true);
    };

    const closeStatusModal = () => {
        setStatusModalOpen(false);
    };

    const handleStatusChange = (status) => {
        fetch(`${BASE_URL}leaves/${selectedLeaveId}/status`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ status }),
        })
            .then((response) => response.json())
            .then((data) => {
                if (data.status === 1) {
                    // Update the status in the list
                    const updatedLeaves = leaves.map((leave) =>
                        leave.id === selectedLeaveId ? { ...leave, status: data.data.status } : leave
                    );

                    setLeaves(updatedLeaves);
                    setFilteredLeaves(updatedLeaves);
                    closeStatusModal();
                    // alert('Leave status updated successfully!');
                } else {
                    alert('Failed to update leave status.');
                }
            })
            .catch((error) => console.error('Error updating leave status:', error));
    };

    return (
        <div id="main">
            <header className="mb-3">
                <a href="#" className="burger-btn d-block d-xl-none">
                    <i className="bi bi-justify fs-3"></i>
                </a>
            </header>

            <div className="page-heading">
                <div className="page-title">
                    <div className="row">
                        <div className="col-12 col-md-6 order-md-1 order-last">
                            <h3>Leave List</h3>
                        </div>
                    </div>
                </div>
            </div>

            <section className="section">
                <div className="row" id="basic-table">
                    <div className="col-12 col-md-12">
                        <div className="card">
                            <div className="card-header">
                                <div className="row">
                                    <div className="col-md-6"></div>
                                    <div className="col-md-6 text-end">
                                        <button
                                            className="btn btn-primary"
                                            onClick={() => navigate('/AddLeave')}
                                        >
                                            Add Leave
                                        </button>
                                        <button
                                            className="btn btn-secondary ms-2"
                                            onClick={handleExportPDF}
                                        >
                                            Export to PDF
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                {/* Date Filters */}
                                <div className="row mb-3">
                                    <div className="col-md-4">
                                        <label htmlFor="searchTerm" className="form-label">
                                            Search by Reason
                                        </label>
                                        <input
                                            type="text"
                                            id="searchTerm"
                                            className="form-control"
                                            placeholder="Search leave reason"
                                            value={searchTerm}
                                            onChange={handleSearchChange}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label htmlFor="startDateFilter" className="form-label">
                                            Start Date
                                        </label>
                                        <input
                                            type="date"
                                            id="startDateFilter"
                                            className="form-control"
                                            value={startDateFilter}
                                            onChange={(e) => setStartDateFilter(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label htmlFor="endDateFilter" className="form-label">
                                            End Date
                                        </label>
                                        <input
                                            type="date"
                                            id="endDateFilter"
                                            className="form-control"
                                            value={endDateFilter}
                                            onChange={(e) => setEndDateFilter(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-4 mt-2">
                                        <label className="form-label">&nbsp;</label>
                                        <button
                                            className="btn btn-secondary"
                                            onClick={handleFilterByDate}
                                        >
                                            Apply Date Filter
                                        </button>
                                    </div>
                                </div>

                                <div className="table-responsive">
                                    <table className="table table-lg">
                                        <thead>
                                            <tr>
                                                <th>STAFF NAME</th> {/* New column */}

                                                <th>START DATE</th>
                                                <th>END DATE</th>
                                                <th>REASON</th>
                                                <th>STATUS</th>
                                                <th className="text-end">ACTION</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredLeaves.map((leave) => (
                                                <tr key={leave.id}>
                                                    <td>{staffDetails[leave.staff_id] || 'N/A'}</td> {/* Display staff name */}

                                                    <td>{formatDate(leave.start_date)}</td>
                                                    <td>{formatDate(leave.end_date)}</td>
                                                    <td>{leave.reason || 'N/A'}</td>
                                                    <td>{leave.status || 'N/A'}</td>
                                                    <td className="text-end">
                                                        <button
                                                            className="btn btn-success"
                                                            onClick={() => handleEdit(leave.id)}
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            className="btn btn-warning ms-2"
                                                            onClick={() => openStatusModal(leave.id)}
                                                        >
                                                            Status
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Status Modal */}
            {statusModalOpen && (
                <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" aria-labelledby="statusModalLabel" aria-hidden="false">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title" id="statusModalLabel">Change Leave Status</h5>
                                <button type="button" className="btn-close" onClick={closeStatusModal}></button>
                            </div>
                            <div className="modal-body text-center">
                                <button className="btn btn-success me-3" onClick={() => handleStatusChange('Approve')}>
                                    Approve
                                </button>
                                <button className="btn btn-danger me-3" onClick={() => handleStatusChange('Reject')}>
                                    Reject
                                </button>
                                <button className="btn btn-warning" onClick={() => handleStatusChange('Pending')}>
                                    Pending
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LeaveList;
